import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Input, Icon, Popover } from 'antd';
import { maskMonthInput, parseMonthInput } from './helpers';

var MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
var VALUE_FORMAT = 'YYYY-MM';
var DISPLAY_FORMAT = 'MM/YYYY';

var toText = function(value) {
  return value ? moment(value, VALUE_FORMAT).format(DISPLAY_FORMAT) : '';
};

/**
 * MonthInput — month field the user can type into ("10/2026") or pick from a
 * month grid via the calendar icon.
 *
 * Props:
 *   - value:        'YYYY-MM' string or null
 *   - onChange:     function(value 'YYYY-MM', moment)
 *   - onPressEnter: optional, called on Enter after a valid month is typed
 *   - placeholder:  default 'MM/AAAA'
 *   - style
 */
var MonthInput = function(props) {
  var value = props.value;
  var onChange = props.onChange;
  var onPressEnter = props.onPressEnter;

  var [text, setText] = useState(toText(value));
  var [invalid, setInvalid] = useState(false);
  var [gridOpen, setGridOpen] = useState(false);
  var [viewYear, setViewYear] = useState(value ? moment(value, VALUE_FORMAT).year() : moment().year());

  useEffect(function() {
    setText(toText(value));
    setInvalid(false);
    if (value) setViewYear(moment(value, VALUE_FORMAT).year());
  }, [value]);

  var commit = function(parsed) {
    setInvalid(false);
    setText(parsed.format(DISPLAY_FORMAT));
    if (onChange && parsed.format(VALUE_FORMAT) !== value) {
      onChange(parsed.format(VALUE_FORMAT), parsed);
    }
  };

  var handleChange = function(e) {
    var masked = maskMonthInput(e.target.value);
    setText(masked);
    setInvalid(false);
    if (masked.length === 7) {
      var parsed = parseMonthInput(masked);
      if (parsed) commit(parsed);
      else setInvalid(true);
    }
  };

  var handleBlur = function() {
    if (!text) {
      setText(toText(value));
      return;
    }
    var parsed = parseMonthInput(text);
    if (parsed) {
      commit(parsed);
    } else {
      // Invalid or incomplete: go back to the last valid month.
      setText(toText(value));
      setInvalid(false);
    }
  };

  var handleKeyDown = function(e) {
    if (e.key !== 'Enter') return;
    var parsed = parseMonthInput(text);
    if (!parsed) {
      setInvalid(true);
      return;
    }
    commit(parsed);
    if (onPressEnter) onPressEnter(parsed.format(VALUE_FORMAT));
  };

  var selectMonth = function(monthIndex) {
    commit(moment({ year: viewYear, month: monthIndex, day: 1 }));
    setGridOpen(false);
  };

  var selected = value ? moment(value, VALUE_FORMAT) : null;
  var today = moment();

  var grid = (
    <div style={{ width: 220 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <button type="button" onClick={function() { setViewYear(viewYear - 1); }} aria-label="Ano anterior" style={{ border: 0, background: 'none', cursor: 'pointer', color: '#1890ff', padding: 4 }}><Icon type="left" /></button>
        <span style={{ fontWeight: 600 }}>{viewYear}</span>
        <button type="button" onClick={function() { setViewYear(viewYear + 1); }} aria-label="Próximo ano" style={{ border: 0, background: 'none', cursor: 'pointer', color: '#1890ff', padding: 4 }}><Icon type="right" /></button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
        {MONTHS.map(function(label, index) {
          var isSelected = selected && selected.year() === viewYear && selected.month() === index;
          var isCurrent = today.year() === viewYear && today.month() === index;
          return (
            <button
              key={label}
              type="button"
              onClick={function() { selectMonth(index); }}
              style={{
                padding: '6px 0', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                border: '1px solid ' + (isSelected ? '#1890ff' : isCurrent ? '#91d5ff' : '#e8e8e8'),
                background: isSelected ? '#1890ff' : '#fff',
                color: isSelected ? '#fff' : '#595959',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );

  var input = (
    <Input
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={props.placeholder || 'MM/AAAA'}
      inputMode="numeric"
      maxLength={7}
      style={Object.assign({ borderColor: invalid ? '#f5222d' : undefined }, props.style)}
      suffix={
        <Popover
          trigger="click"
          placement="bottomRight"
          visible={gridOpen}
          onVisibleChange={setGridOpen}
          content={grid}
          getPopupContainer={function(trigger) { return trigger.parentNode; }}
        >
          <Icon type="calendar" style={{ color: '#8c8c8c', cursor: 'pointer' }} aria-label="Escolher mês" />
        </Popover>
      }
    />
  );

  return (
    <div style={{ flex: props.style && props.style.flex, minWidth: 0 }}>
      {input}
      {invalid && (
        <div role="alert" style={{ color: '#f5222d', fontSize: 11, marginTop: 2 }}>
          Mês inválido. Use MM/AAAA
        </div>
      )}
    </div>
  );
};

export default MonthInput;
